import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Images, Info, AlertTriangle, Upload } from "lucide-react";

import { useImageImport } from "./useImageImport";
import { useImageUpload } from "./useImageUpload";
import ImportResults from "./ImportResults";
import UploadFileList from "./UploadFileList";
import "./ImportImages.css";

// Shown near the action — these two notes prevent the most common support
// tickets (everything landing in "unmatched", and "replace" surprises).
const OperatorNotes = () => (
  <div className="pii-notes">
    <div className="pii-note">
      <Info size={16} aria-hidden="true" />
      <span>
        <strong>Promote products live first, then import.</strong> Images only
        attach to live products. If products are still in staging, every image
        lands in <em>unmatched</em>.
      </span>
    </div>
    <div className="pii-note">
      <Info size={16} aria-hidden="true" />
      <span>
        <strong>Name every file by its product SKU</strong> (e.g.{" "}
        <code>2391800012.jpg</code>). Files with spaces or no SKU are rejected.
      </span>
    </div>
  </div>
);

const WAITING_LABEL = {
  PENDING: "Queued, waiting…",
  STARTED: "Starting…",
};

// Rotating reassurance shown while the backend is busy, so a long job never
// reads as "stuck". Cycles every ~5s based on elapsed time.
const WORKING_MESSAGES = [
  "Matching images to products by SKU…",
  "Attaching covers to matched products…",
  "Large batches can take a few minutes — this keeps running in the background.",
  "You can keep this tab open; the summary appears here when it’s done.",
];

const formatElapsed = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const ProgressView = ({ progress, lifecycle }) => {
  // Real percentage when the backend reports counts; otherwise null (a small/
  // fast job may finish before it ever emits a PROGRESS frame).
  const realPct =
    progress?.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;

  // Always-determinate bar. While there's no real count yet, ease toward ~90%
  // so it visibly fills instead of sliding; snap to the real value once it
  // arrives, and never move backward.
  const [shown, setShown] = useState(6);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (realPct != null) {
      setShown((prev) => Math.max(prev, realPct));
      return undefined;
    }
    const id = setInterval(
      () => setShown((s) => (s >= 90 ? s : Math.round(s + (90 - s) * 0.07 + 0.4))),
      400
    );
    return () => clearInterval(id);
  }, [realPct]);

  // Elapsed timer — a value that changes every second is the strongest signal
  // that the job is alive, not frozen.
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const message = WORKING_MESSAGES[Math.floor(elapsed / 5) % WORKING_MESSAGES.length];

  return (
    <div className="pii-progress">
      <div className="pii-progress__head">
        <span className="pii-progress__label">
          {realPct == null
            ? WAITING_LABEL[lifecycle] || "Starting the import…"
            : "Importing images"}
        </span>
        <span className="pii-progress__pct">{shown}%</span>
      </div>

      <div
        className="pii-bar pii-bar--working"
        role="progressbar"
        aria-valuenow={shown}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="pii-bar__fill" style={{ width: `${shown}%` }} />
      </div>

      {progress && (
        <div className="pii-progress__sub">
          {progress.done.toLocaleString()} of {progress.total.toLocaleString()} files processed
        </div>
      )}

      <div className="pii-working">
        <span className="pii-working__pulse" aria-hidden="true" />
        <span className="pii-working__msg">{message}</span>
        <span className="pii-working__time">Working · {formatElapsed(elapsed)}</span>
      </div>

      {progress && (
        <div className="pii-progress__counts">
          <span className="pii-chip pii-chip--success">Imported {progress.imported ?? 0}</span>
          <span className="pii-chip">Skipped {progress.skipped ?? 0}</span>
          <span className="pii-chip pii-chip--warning">Unmatched {progress.unmatched ?? 0}</span>
          <span className="pii-chip pii-chip--danger">Errors {progress.errors ?? 0}</span>
        </div>
      )}
    </div>
  );
};

const ErrorView = ({ message, onRetry }) => (
  <div className="pii-error">
    <AlertTriangle size={32} aria-hidden="true" />
    <p className="pii-error__message">{message}</p>
    <button type="button" className="pii-btn pii-btn--primary" onClick={onRetry}>
      Try again
    </button>
  </div>
);

const ImportImagesPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [overwrite, setOverwrite] = useState(false);

  const upload = useImageUpload();
  const job = useImageImport();

  const { stats, isUploading } = upload;
  const jobStarted = job.phase !== "idle";

  const handlePick = (e) => {
    if (e.target.files?.length) upload.addFiles(e.target.files);
    e.target.value = ""; // allow re-picking the same file after a removal
  };

  // Upload everything to S3 first; only start the job once every file is in.
  const handleStart = async () => {
    const allUploaded = await upload.uploadAll();
    if (allUploaded) job.start(overwrite);
  };

  const startLabel = isUploading
    ? `Uploading ${stats.done}/${stats.uploadable}…`
    : stats.failed > 0
    ? "Retry & start import"
    : "Upload & start import";

  return (
    <div className="pii-page">
      <header className="pii-header">
        <button
          type="button"
          className="pii-back"
          onClick={() => navigate("/product")}
          aria-label="Back to products"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="pii-header__title-block">
          <h1 className="pii-title">
            <Images size={20} aria-hidden="true" /> Import product images
          </h1>
          <p className="pii-subtitle">
            Pick SKU-named images, upload them, and attach each as a product
            cover in one background job.
          </p>
        </div>
      </header>

      <div className="pii-card">
        {!jobStarted && (
          <>
            <OperatorNotes />

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="pii-hidden-input"
              onChange={handlePick}
            />
            <button
              type="button"
              className="pii-dropzone"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload size={22} aria-hidden="true" />
              <span className="pii-dropzone__title">Choose images</span>
              <span className="pii-dropzone__hint">
                Each file must be named by its product SKU
              </span>
            </button>

            {isUploading && (
              <div className="pii-progress pii-progress--upload">
                <div className="pii-progress__head">
                  <span className="pii-progress__label">Uploading images</span>
                  <span className="pii-progress__pct">{stats.uploadPct}%</span>
                </div>
                <div className="pii-bar">
                  <div className="pii-bar__fill" style={{ width: `${stats.uploadPct}%` }} />
                </div>
                <div className="pii-progress__sub">
                  {stats.done} of {stats.uploadable} uploaded
                </div>
              </div>
            )}

            {stats.total > 0 && !isUploading && (
              <div className="pii-files-summary">
                <span>
                  {stats.total} file{stats.total === 1 ? "" : "s"}
                  {stats.failed > 0 && ` · ${stats.failed} need attention`}
                </span>
                <button
                  type="button"
                  className="pii-link-btn"
                  onClick={upload.clearFiles}
                >
                  Clear all
                </button>
              </div>
            )}

            <UploadFileList
              files={upload.files}
              onRemove={upload.removeFile}
              busy={isUploading}
            />

            <label className="pii-checkbox">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={isUploading}
              />
              <span>
                Replace existing photos
                <small>
                  Off by default — products that already have a photo are
                  skipped. Tick to force-replace them.
                </small>
              </span>
            </label>

            <div className="pii-actions">
              <button
                type="button"
                className="pii-btn pii-btn--primary"
                onClick={handleStart}
                disabled={!stats.canStart}
              >
                {startLabel}
              </button>
            </div>
          </>
        )}

        {(job.phase === "starting" || job.phase === "running") && (
          <ProgressView progress={job.progress} lifecycle={job.lifecycle} />
        )}

        {job.phase === "success" && (
          <>
            <ImportResults summary={job.summary} />
            <div className="pii-actions">
              <button
                type="button"
                className="pii-btn pii-btn--ghost"
                onClick={() => {
                  upload.clearFiles();
                  job.reset();
                }}
              >
                Run another import
              </button>
              <button
                type="button"
                className="pii-btn pii-btn--primary"
                onClick={() => navigate("/product")}
              >
                Back to products
              </button>
            </div>
          </>
        )}

        {job.phase === "failure" && (
          <ErrorView message={job.error} onRetry={job.reset} />
        )}
      </div>
    </div>
  );
};

export default ImportImagesPage;
